import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  GroupType,
  type CreateCostOffsetCodeMutation,
  type CreateCostOffsetCodeMutationVariables,
} from 'graphql-query-contracts';
import { createCostOffsetCodeMutation } from 'graphql-queries';
import { CreateCostOffsetCodeError } from './errors/CreateCostOffsetCodeError';

export interface CreateCostOffsetCodeInput {
  code: string;
  description?: string;
  groupId: string;
  groupType: GroupType;
}

export interface CreateCostOffsetCodeResult {
  id: string;
}

export interface UseCreateCostOffsetCodeResult {
  createCostOffsetCode: (
    input: CreateCostOffsetCodeInput
  ) => Promise<CreateCostOffsetCodeResult>;
  loading: boolean;
  error: Error | undefined;
}

export function useCreateCostOffsetCode(): UseCreateCostOffsetCodeResult {
  const [mutate, { loading, error }] = useMutation<
    CreateCostOffsetCodeMutation,
    CreateCostOffsetCodeMutationVariables
  >(createCostOffsetCodeMutation);

  const createCostOffsetCode = useCallback(
    async (
      input: CreateCostOffsetCodeInput
    ): Promise<CreateCostOffsetCodeResult> => {
      try {
        const result = await mutate({
          variables: {
            createCostOffsetCodeRequest: {
              code: input.code,
              description: input.description,
              groupId: input.groupId,
              groupType: input.groupType,
            },
          },
        });

        const createdId = result.data?.accounts?.createCostOffsetCode?.id;

        if (!createdId) {
          throw new CreateCostOffsetCodeError();
        }

        return { id: createdId };
      } catch (err) {
        if (err instanceof CreateCostOffsetCodeError) {
          throw err;
        }

        throw new CreateCostOffsetCodeError(
          err instanceof Error ? err.message : undefined,
          { cause: err }
        );
      }
    },
    [mutate]
  );

  return {
    createCostOffsetCode,
    loading,
    error: error ?? undefined,
  };
}
